// IA uses different and more complicated message data structure than the rest of the chat.
// Each message contains a list of things to render and a couple of flags.
// This file handles all this magic.

import { deepChange } from "../../actions";
import { getLatestState, getProviderID, getUserID } from "../../store";
import { postQuestion, requestGreeting } from "./api";
import { MessageTypes } from "../../lib/types";
import { getIsTooWrong, getWrongAnswers } from "./selectors";
import { defaultErrorMessage, typingIADuration } from "../../lib/constants";
import { addMessage } from "../messages/actions";
import { saveLastIaQuestion, setGreetedUser, updateWrongAnswers } from "./actions";

const TEXT = "text";
const ERROR_MSG = {
  values: [
    {
      type: TEXT,
      data: defaultErrorMessage,
    },
  ],
  meta: {
    askIfUseful: false,
    messageType: "ERROR",
  },
};

/**
 * Add IA user message to store.
 * @param data - message data with `values` and `meta` fields.
 * Gonna add flow.js here at some point.
 * @returns {Function}
 */
export const addQuestion = (data) => (dispatch) => {
  dispatch(
    addMessage({
      type: MessageTypes.IA_USER_MESSAGE,
      data,
    })
  );
};

/**
 * Store IA bot message in the store.
 * @param data - message data.
 * @returns {Function}
 */
export const addAnswer = (data) => (dispatch, getState) => {
  const { values = [], action = null } = data;
  const state = getState();
  const messageQueue = [];
  const isTooWrong = getIsTooWrong(state);

  if (values?.length > 0) {
    values.forEach((value, i) => {
      const isLast = i === values.length - 1;
      const hasFeedbackPrompt = isLast && data.meta?.askIfUseful;

      const answerMessage = {
        type: MessageTypes.IA_BOT_MESSAGE,
        data: { values: [value], action: isLast ? action : null },
      };

      if (hasFeedbackPrompt) {
        Object.assign(answerMessage.data, { meta: { hasFeedbackPrompt } });
      }

      const delayedMessage = setTimeout(() => {
        dispatch(addMessage(answerMessage));

        if (isLast) {
          // User was displayed all welcoming messages
          dispatch(setGreetedUser());
        }
      }, i * typingIADuration);

      messageQueue.push(delayedMessage);
    });
  }

  if (data.suggested_questions?.length > 0) {
    dispatch(deepChange({ "ia.suggestions": data.suggested_questions }));
  }

  // Append FEEDBACK message containing affirmative and negative usefulness actions
  if (data.meta?.askIfUseful || isTooWrong) {
    const delayedFeedbackMessage = setTimeout(() => {
      const feedbackMessage = {
        type: MessageTypes.IA_FEEDBACK_MESSAGE,
        data, // Passing the same data here - required to send the proper feedback to the server.
      };

      dispatch(addMessage(feedbackMessage));
      dispatch(deepChange({ "ia.showQuickActions": true }));
    }, values.length * typingIADuration);

    messageQueue.push(delayedFeedbackMessage);
  }

  dispatch(deepChange({ "ia.queue": messageQueue }));
  // TODO: in the original IA demo repo this is never getting reset. It this fine?
};

export const getGreetingMessage = () => async (dispatch, getState) => {
  try {
    const response = await requestGreeting(getProviderID(getState()));
    dispatch(addAnswer({ values: response }));
  } catch (e) {
    console.error(e);
    dispatch(addAnswer(ERROR_MSG));
  }
};

export const sendMessage = (msg) => async (dispatch, getState) => {
  dispatch(
    addQuestion({
      values: [
        {
          type: TEXT,
          data: msg,
        },
      ],
      meta: {},
    })
  );

  dispatch(saveLastIaQuestion(msg));

  // Reset that if we were too wrong before.
  if (getIsTooWrong(getState())) {
    dispatch(deepChange({ "ia.wrongAnswersInRow": 0 }));
  }

  const state = getState();

  const request = {
    question: msg,
    provider_profile_id: getProviderID(state),
    chat_user_uuid: getUserID(state),
  };

  try {
    const response = await postQuestion(request);
    dispatch(postQuestionSuccess(response));
  } catch (e) {
    console.error(e);
    dispatch(addAnswer(ERROR_MSG));
  }
};

/**
 * Process response received from server.
 * Fully copied from Irfan's code.
 * @param response
 * @returns {Function}
 */
const postQuestionSuccess = (response) => (dispatch) => {
  const extractAnswer = (response) => {
    try {
      const { question, answer, suggested_questions } = response;
      const askIfUseful = !!question;

      if (answer) {
        return {
          question,
          action: answer.action,
          values: answer.values,
          suggested_questions,
          meta: {
            askIfUseful,
          },
        };
      }

      const state = dispatch(getLatestState);
      const wrongAnswers = getWrongAnswers(state);
      dispatch(updateWrongAnswers(wrongAnswers + 1));
      return {
        values: [
          {
            type: TEXT,
            data: "Sorry, we didn't understand your question. Would you mind rephrasing it, please?",
          },
        ],
        suggested_questions: [],
        meta: {
          askIfUseful: false,
        },
      };
    } catch (e) {
      return ERROR_MSG;
    }
  };

  dispatch(addAnswer(extractAnswer(response)));
};
